import { FocusKeyManager } from '@angular/cdk/a11y';
import { ENTER, SPACE } from '@angular/cdk/keycodes';
import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChildren,
  effect,
  forwardRef,
  HostBinding,
  input,
  OnDestroy,
  output,
  signal,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { Subscription } from 'rxjs';
import { HxBaseControl } from '../../base';
import { HxFormGroup } from '../form-group';
import { HxLabel } from '../label/label';

import { HxInput } from '../input';
import { HxChip } from './chip';

export enum HxChipType {
  Standard = 'standard',
  None = 'None',
  All = 'All',
  Other = 'Other',
}

export interface ChipData {
  value?: string;
  label: string;
  variant?: 'fill' | 'soft' | 'outline';
  disabled?: boolean;
  selected?: boolean;
}

export type HxChipSelectionChange = {
  source: HxChipGroup;
  value: string[] | string | null;
};

@Component({
  selector: 'hx-chip-group',
  standalone: true,
  imports: [HxChip, HxFormGroup, HxInput, ReactiveFormsModule, HxLabel],
  templateUrl: './chip-group.html',
  styleUrl: './chip-group.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => HxChipGroup),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => HxChipGroup),
      multi: true,
    },
  ],
  host: {
    '[class.hx-chip-group]': 'true',
    '[class.hx-chip-group--grid]': 'layout() === "grid"',
    '[class.hx-chip-group--align-left]': 'align() === "left"',
    '[class.hx-chip-group--align-center]': 'align() === "center"',
    '[class.hx-chip-group--align-right]': 'align() === "right"',
    '[style.--chip-group-columns]': '_getValidColumns()',
  },
})
export class HxChipGroup extends HxBaseControl implements AfterContentInit, OnDestroy, Validator {
  readonly multiple = input<boolean | undefined>(false);
  readonly size = input<'sm' | 'md' | 'lg' | undefined>('md');
  readonly variant = input<'fill' | 'soft' | 'outline' | undefined>('outline'); // Default variant for all chips
  readonly chipsData = input<ChipData[]>([]);
  readonly minimum = input<number | undefined>(0);
  readonly maximum = input<number | undefined>(0); // US8: Maximum number of chips that can be selected
  readonly label = input<string | undefined>('');
  readonly sublabel = input<string | undefined>('');
  readonly hint = input<string | undefined>('');
  readonly requiredErrorMessage = input<string | undefined>(''); // Custom error message for required validation
  readonly minimumErrorMessage = input<string | undefined>(''); // US7: Custom error message for minimum validation
  readonly maximumErrorMessage = input<string | undefined>(''); // US8: Custom error message for maximum validation
  readonly otherLabel = input<string | undefined>('Other'); // US9: Custom label for Other chip
  readonly otherPlaceholder = input<string | undefined>('Enter custom value'); // US9: Placeholder for Other input
  readonly otherExclusive = input<boolean | undefined>(false); // US9: If true, selecting Other deselects all others
  readonly showNoneChip = input<boolean | undefined>(false); // Show "None" chip to reset selections
  readonly showAllChip = input<boolean | undefined>(false); // Show "All" chip to select all options
  readonly showOtherChip = input<boolean | undefined>(false); // Show "Other" chip for custom input
  readonly showInputOnOther = input<boolean | undefined>(true); // Show input field when "Other" is selected (default: true)
  readonly otherChipLabel = input<string | undefined>('Other'); // US9: Label for the "Other" chip
  readonly otherChipPlaceholder = input<string | undefined>('Please specify'); // US9: Placeholder for Other input field
  readonly otherInputRequired = input<boolean | undefined>(false); // US9: Whether the "Other" input field is required
  readonly otherDisabled = input<boolean | undefined>(false); // Disable the "Other" chip + its input via external rules
  readonly otherHidden = input<boolean | undefined>(false); // Hide the "Other" chip entirely via external rules
  readonly layout = input<'default' | 'grid' | undefined>('default'); // US11: Layout mode (default or grid)
  readonly columns = input<number | undefined>(3); // US11: Number of columns when layout='grid' (1-5, default: 3)
  readonly align = input<'left' | 'center' | 'right' | undefined>('left'); // US12: Horizontal alignment of chip group

  readonly selectionChange = output<HxChipSelectionChange>();
  readonly otherValueChange = output<string>(); // Emit when "Other" input field value changes

  readonly projectedChips = contentChildren(HxChip);
  protected readonly _value = signal<string[] | string | null>(null);
  protected readonly _error = signal<string | null>(null);
  protected readonly _touched = signal<boolean>(false); // Track if control has been touched
  protected readonly _otherValue = signal<string>(''); // US9: Store the "Other" input text
  protected readonly _otherSelected = signal<boolean>(false); // US9: Track if "Other" chip is selected
  protected readonly _otherFormControl = new FormControl<string>(''); // US9: FormControl for "Other" input field
  private _isBlockedByMax = signal<boolean>(false);

  private _keyManager: FocusKeyManager<any> | null = null;
  private _chipSubscriptions = new Subscription();
  protected override _onChange: (value: string[] | string | null) => void = () => {};
  protected override _onTouched: () => void = () => {};

  // inside HxChipGroup class

  protected readonly _processedChips = computed(() => {
    const chips = [...this.chipsData()];
    const max = this.maximum();
    const multiple = this.multiple();
    // Get default variant from input or first chip's variant
    const defaultVariant = this.variant() || (chips.length > 0 && chips[0].variant) || 'outline';

    // 1. Logic for "All" Chip (placed ahead of regular options)
    if (this.showAllChip() && multiple) {
      const standardCount = this.chipsData().filter(
        (c) =>
          c.label.toLowerCase() !== HxChipType.All.toLowerCase() &&
          c.label.toLowerCase() !== HxChipType.None.toLowerCase()
      ).length;
      const shouldDisableAll = max > 0 && standardCount > max;

      chips.unshift({
        value: HxChipType.All,
        label: HxChipType.All,
        variant: defaultVariant,
        disabled: shouldDisableAll,
      });
    }

    // 2. Logic for "None" Chip (always first when enabled)
    if (this.showNoneChip() && multiple) {
      chips.unshift({ value: HxChipType.None, label: HxChipType.None, variant: defaultVariant });
    }

    // 3. Logic for "Other" Chip (always last)
    if (this.showOtherChip() && !this.otherHidden()) {
      chips.push({
        value: this.otherLabel() || HxChipType.Other,
        label: this.otherLabel() || HxChipType.Other,
        variant: defaultVariant,
        disabled: this.otherDisabled(),
      });
    }

    return chips;
  });

  @HostBinding('attr.role') get role() {
    return 'listbox';
  }

  /** US11: Get validated columns value for CSS variable */
  protected _getValidColumns(): string | null {
    if (this.layout() !== 'grid') return null;
    const cols = this.columns();
    // Clamp value between 1 and 5
    const validCols = Math.max(1, Math.min(5, cols));
    if (cols !== validCols) {
      console.warn(`[HxChipGroup] columns value ${cols} is out of range (1-5). Using ${validCols}.`);
    }
    return validCols.toString();
  }
  @HostBinding('attr.aria-disabled') get ariaDisabled() {
    return this.disabledState() ? 'true' : null;
  }
  /** Helper method to check if a chip is selected - reactive to _value signal */
  protected _isSelected(value: string): boolean {
    const val = this._value();
    return Array.isArray(val) ? val.includes(value) : val === value;
  }

  private _getChipValue(chip: ChipData): string {
    return chip.value ?? chip.label;
  }

  constructor() {
    super();

    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }

    // Initialize pre-selected chips from chipsData
    effect(() => {
      const chips = this.chipsData();
      const selectedChips = chips.filter((chip) => chip.selected).map((chip) => this._getChipValue(chip));

      if (selectedChips.length > 0) {
        const initialValue = this.multiple() ? selectedChips : selectedChips[0];
        // Only set if _value is not already set
        if (!this._value()) {
          this._value.set(initialValue);
        }
      }
    });

    // Sync UI state with internal _value signal
    effect(() => {
      const projectedChips = this.projectedChips();
      const value = this._value();
      projectedChips.forEach((chip) => {
        const chipLabel = chip.label();
        if (this.multiple()) {
          chip.selected.set(Array.isArray(value) && value.includes(chipLabel));
        } else {
          chip.selected.set(value === chipLabel);
        }
      });
    });
    // Setup listeners for projected chips
    effect(() => {
      const projectedChips = this.projectedChips();
      this._chipSubscriptions.unsubscribe();
      this._chipSubscriptions = new Subscription();
      projectedChips.forEach((chip) => {
        this._chipSubscriptions.add(chip.selectedChange.subscribe(() => this._handleProjectedChipChange(chip)));
      });
    });
    // Validate minimum and maximum
    effect(() => {
      const val = this._value();
      const count = Array.isArray(val) ? val.length : val ? 1 : 0;
      this._runValidation(count);
    });

    effect(() => {
      void this.required();
      void this.minimum();
      void this.maximum();
      void this.multiple();
      this.notifyValidatorChange();
    });

    // Set up validators for the Other input field based on otherInputRequired input
    effect(() => {
      void this.otherInputRequired();
      void this._otherSelected();
      this._syncOtherValidators();
    });

    // A rule can disable/hide "Other" while it's selected - deselect it and clear its text reactively
    effect(() => {
      if ((this.otherDisabled() || this.otherHidden()) && this._otherSelected()) {
        this._deselectOther();
      }
    });
  }

  ngAfterContentInit(): void {
    this._keyManager = new FocusKeyManager(this.projectedChips() as any).withWrap().withHomeAndEnd();
  }

  ngOnDestroy(): void {
    this._keyManager?.destroy();
    this._chipSubscriptions.unsubscribe();
  }

  /** Handle change from projected chips (ng-content) */
  private _handleProjectedChipChange(chip: HxChip): void {
    if (this.disabled() || this.readonly() || chip.disabled()) return;

    const label = chip.label();
    const isSelected = chip.selected();
    const val = this._value();
    let current = Array.isArray(val) ? [...(val as string[])] : [];

    // Reset the blocked flag at the start of any interaction attempt
    this._isBlockedByMax.set(false);

    // --- SINGLE SELECT MODE ---
    if (!this.multiple()) {
      if (isSelected) {
        // Manually deselect all other projected chips to maintain single-select enforcement
        this.projectedChips().forEach((c) => {
          if (c !== chip) c.selected.set(false);
        });
        this._updateValue(label);
      } else {
        this._updateValue(null);
      }
      return;
    }

    // --- MULTI SELECT MODE ---

    // A. Handle "None" Chip - when showNoneChip is true and selected chip is the noneLabel
    if (this.showNoneChip() && label.toLowerCase() === HxChipType.None.toLowerCase()) {
      if (isSelected) {
        // Selecting "None" deselects everything else (UI and Model)
        this.projectedChips().forEach((c) => {
          if (c !== chip) c.selected.set(false);
        });
        this._otherSelected.set(false);
        current = [label];
      } else {
        current = [];
      }
    }

    // B. Handle "All" Chip - when showAllChip is true and selected chip is the allLabel
    else if (this.showAllChip() && label.toLowerCase() === HxChipType.All.toLowerCase()) {
      const standardEnabled = this.projectedChips().filter(
        (c) =>
          c !== chip &&
          !c.disabled() &&
          !(this.showNoneChip() && c.label().toLowerCase() === HxChipType.None.toLowerCase())
      );

      // US8: Check if "All" exceeds maximum limit before allowing selection
      if (isSelected && this.maximum() > 0 && standardEnabled.length > this.maximum()) {
        chip.selected.set(false); // Force the checkbox/chip back to unselected in UI
        this._isBlockedByMax.set(true); // Flag for manual error display
        this._runValidation(current.length); // Trigger validation manually
        return;
      }

      // Toggle standard chips based on the "All" chip state
      standardEnabled.forEach((c) => c.selected.set(isSelected));
      current = isSelected ? [...standardEnabled.map((c) => c.label()), label] : [];
    }

    // C. Handle Standard & Other Chips
    else {
      const index = current.indexOf(label);
      const isAdding = isSelected && index === -1;

      // US8: Block selection if it would exceed Max Selections
      if (isAdding && this.maximum() > 0 && current.length >= this.maximum()) {
        chip.selected.set(false); // Revert the individual chip's UI state
        this._isBlockedByMax.set(true); // Flag for manual error display
        this._runValidation(current.length); // Trigger validation manually
        return;
      }

      if (isSelected && index === -1) {
        current.push(label);
        if (this.showOtherChip() && label === this.otherLabel()) {
          this._otherSelected.set(true);
          this._syncOtherValidators();
        }
      } else if (!isSelected && index > -1) {
        current.splice(index, 1);
        if (this.showOtherChip() && label === this.otherLabel()) {
          this._otherSelected.set(false);
          this._syncOtherValidators();
        }
      }

      // US5: If a standard chip is now selected, find and deselect the "None" chip
      if (this.showNoneChip()) {
        const noneChip = this.projectedChips().find((c) => c.label().toLowerCase() === HxChipType.None.toLowerCase());
        if (isSelected && noneChip) {
          noneChip.selected.set(false);
          current = current.filter((v) => v.toLowerCase() !== HxChipType.None.toLowerCase());
        }
      }

      // US6: Auto-calculate "All" chip state based on standard chip selections
      if (this.showAllChip()) {
        const allChip = this.projectedChips().find((c) => c.label().toLowerCase() === HxChipType.All.toLowerCase());
        if (allChip) {
          const standards = this.projectedChips().filter(
            (c) =>
              c.label().toLowerCase() !== HxChipType.All.toLowerCase() &&
              c.label().toLowerCase() !== HxChipType.None.toLowerCase() &&
              c.label() !== this.otherLabel() &&
              !c.disabled()
          );
          const everyStandardPicked = standards.length > 0 && standards.every((c) => c.selected());

          allChip.selected.set(everyStandardPicked);
          if (everyStandardPicked) {
            if (!current.includes('All')) current.push('All');
          } else {
            current = current.filter((v) => v !== 'All');
          }
        }
      }
    }

    this._updateValue(current.length > 0 ? [...new Set(current)] : null);
  }

  // inside HxChipGroup class

  protected _handleChipClick(chip: ChipData): void {
    // 1. Guard for disabled or readonly state
    if (this.disabled() || this.readonly() || chip.disabled) return;

    const label = chip.label;
    const chipValue = this._getChipValue(chip);
    const val = this._value();
    let current = Array.isArray(val) ? [...(val as string[])] : [];
    const isCurrentlySelected = current.includes(chipValue);

    // ALWAYS reset the blocked flag at the start of any interaction attempt.
    // This ensures that clicking a selected chip (to deselect) clears previous errors.
    this._isBlockedByMax.set(false);

    // --- MULTI SELECT MODE ---
    if (this.multiple()) {
      // A. Handle "None" Chip when showNoneChip is true
      if (this.showNoneChip() && label.toLowerCase() === HxChipType.None.toLowerCase()) {
        // Selecting "None" clears everything else
        current = !isCurrentlySelected ? [chipValue] : [];
        this._otherSelected.set(false);
      }
      // B. Scenario: User clicks any other chip (All, Other, or Standard)
      else {
        // A: If ANY other chip is clicked, "None" MUST be removed from the selection array
        if (this.showNoneChip()) {
          current = current.filter((v) => v.toLowerCase() !== HxChipType.None.toLowerCase());
        }

        // B1: User clicks "All" when showAllChip is true
        if (this.showAllChip() && label.toLowerCase() === HxChipType.All.toLowerCase()) {
          const standards = this.chipsData().filter(
            (c) =>
              c.label.toLowerCase() !== HxChipType.All.toLowerCase() &&
              c.label.toLowerCase() !== HxChipType.None.toLowerCase() &&
              c.label !== this.otherLabel() &&
              !c.disabled
          );

          // US8 Scenario 3: Block "All" if selecting everything would violate the maximum limit
          if (!isCurrentlySelected && this.maximum() > 0 && standards.length > this.maximum()) {
            chip.selected = false;
            this._isBlockedByMax.set(true);
            this._runValidation(current.length);
            return;
          }

          // Toggle: If "All" is selected, clear everything. If not, select all standards + "All".
          current = isCurrentlySelected ? [] : [...standards.map((c) => this._getChipValue(c)), chipValue];
          this._otherSelected.set(!isCurrentlySelected && !!standards.find((s) => s.label === this.otherLabel()));
        }
        // B2: User clicks a "Standard" or "Other" chip
        else {
          // US8 Scenario 1: Block selection if at limit and trying to add a new chip
          if (!isCurrentlySelected && this.maximum() > 0 && current.length >= this.maximum()) {
            this._isBlockedByMax.set(true);
            this._runValidation(current.length); // Trigger validation manually
            return;
          }

          if (!isCurrentlySelected) {
            // Adding selection
            current.push(chipValue);
            if (this.showOtherChip() && label === this.otherLabel()) {
              this._otherSelected.set(true);
              this._syncOtherValidators();
            }
          } else {
            // Removing selection
            current = current.filter((v) => v !== chipValue);
            if (this.showOtherChip() && label === this.otherLabel()) {
              this._otherSelected.set(false);
              this._syncOtherValidators();
            }
          }

          // US6: Auto-update "All" chip state based on current selections
          if (this.showAllChip()) {
            const standards = this.chipsData().filter(
              (c) =>
                c.label.toLowerCase() !== HxChipType.All.toLowerCase() &&
                c.label.toLowerCase() !== HxChipType.None.toLowerCase() &&
                c.label !== this.otherLabel() &&
                !c.disabled
            );
            const allStandardsPicked =
              standards.length > 0 && standards.every((s) => current.includes(this._getChipValue(s)));

            if (allStandardsPicked) {
              if (!current.includes(HxChipType.All)) current.push(HxChipType.All);
            } else {
              current = current.filter((v) => v.toLowerCase() !== HxChipType.All.toLowerCase());
            }
          }
        }
      }
      // Finalize update - use Set to ensure no duplicate labels
      this._updateValue(current.length > 0 ? [...new Set(current)] : null);
    }
    // --- SINGLE SELECT MODE ---
    else {
      const isCurrentlySelected = this._value() === chipValue;
      this._updateValue(isCurrentlySelected ? null : chipValue);
      this._otherSelected.set(
        this.showOtherChip() && label.toLowerCase() === this.otherLabel().toLowerCase() && !isCurrentlySelected
      );
    }
  }

  /** Unified Validation Logic */
  private _runValidation(count: number) {
    // Only show validation errors if the control has been touched
    if (!this._touched()) {
      this._error.set(null);
      return;
    }
    // Check required first (applies to both single and multiple modes)
    if (this.required() && count === 0) {
      this._error.set(this.requiredErrorMessage() || 'Please select at least one option.');
      return;
    }

    if (!this.multiple()) {
      this._error.set(null);
      return;
    }
    if (this._isBlockedByMax()) {
      this._error.set(this.maximumErrorMessage() || `Please select at most ${this.maximum()} option(s).`);
      return;
    }

    // Check if current selection (in the model) is invalid
    if (this.maximum() > 0 && count > this.maximum()) {
      this._error.set(this.maximumErrorMessage() || `Please select at most ${this.maximum()} option(s).`);
    } else if (count < this.minimum()) {
      this._error.set(this.minimumErrorMessage() || `Please select at least ${this.minimum()} option(s).`);
    } else {
      // Only clear the error if we aren't currently showing a "Blocked" error from a click attempt
      // If the count is valid, we clear it.
      this._error.set(null);
    }
  }

  private _updateValue(value: string[] | string | null): void {
    this._value.set(value);
    this._touched.set(true); // Mark as touched when value changes
    this._onChange(value);
    this._onTouched(); // Notify form control that field was touched
    this.notifyValidatorChange();
    this.selectionChange.emit({ source: this, value });
  }

  // US9: Handle "Other" input field value changes
  _handleOtherInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this._otherValue.set(input.value);
    this._otherFormControl.setValue(input.value);
    this._otherFormControl.updateValueAndValidity({ emitEvent: false });
    this.notifyValidatorChange();
    this.otherValueChange.emit(input.value);
  }

  private _syncOtherValidators(): void {
    if (this.otherInputRequired() && this._otherSelected()) {
      this._otherFormControl.setValidators(Validators.required);
      if (this._touched()) {
        this._otherFormControl.markAsTouched({ emitEvent: false });
      }
    } else {
      this._otherFormControl.clearValidators();
      this._otherFormControl.markAsUntouched({ emitEvent: false });
    }
    this._otherFormControl.updateValueAndValidity({ emitEvent: false });
  }

  // US9: Handle "Other" input focus loss (Escape key handling via template)
  _handleOtherInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this._deselectOther();
    }
  }

  /** Deselect the "Other" chip/value and reset its input, used for both Escape and rule-driven disable/hide */
  private _deselectOther(): void {
    this._otherSelected.set(false);
    this._otherFormControl.reset('', { emitEvent: false });
    const val = this._value();
    if (Array.isArray(val)) {
      const current = val.filter((v) => v !== this.otherLabel());
      this._updateValue(current.length > 0 ? current : null);
    } else if (val === this.otherLabel()) {
      this._updateValue(null);
    }
  }

  _handleKeydown(event: KeyboardEvent): void {
    if (this.disabled() || this.readonly()) return;
    this._keyManager?.onKeydown(event);
    if (event.keyCode === SPACE || event.keyCode === ENTER) {
      const active = this.chipsData()[this._keyManager?.activeItemIndex ?? -1];
      if (active) this._handleChipClick(active);
    }
  }

  protected isLimitReached(): boolean {
    const val = this._value();
    const count = Array.isArray(val) ? val.length : val ? 1 : 0;
    return this.multiple() && this.maximum() > 0 && count >= this.maximum();
  }

  /** Validates the chip group value (for required validation) */
  protected _validate(control: AbstractControl): ValidationErrors | null {
    const val = this._value();
    const count = Array.isArray(val) ? val.length : val ? 1 : 0;
    const isTouched = control.touched || this._touched();

    if (!isTouched) {
      return null;
    }

    if (this.required() && count === 0) {
      return { required: true };
    }

    if (this.otherInputRequired() && this._otherSelected() && this._otherFormControl.invalid) {
      return { otherRequired: true };
    }

    if (this.multiple()) {
      if (this.maximum() > 0 && (count > this.maximum() || this._isBlockedByMax())) {
        return { maxSelection: { required: this.maximum(), actual: count } };
      }

      if (this.minimum() > 0 && count < this.minimum()) {
        return { minSelection: { required: this.minimum(), actual: count } };
      }
    }

    return null;
  }

  override writeValue(value: any) {
    const otherLabelVal = this.otherLabel() || HxChipType.Other;
    if (Array.isArray(value)) {
      const predefinedValues = new Set(
        this.chipsData()
          .map((c) => this._getChipValue(c))
          .filter((v) => v !== otherLabelVal)
      );

      let customValue = '';
      const cleanedValues = value.map((v) => {
        const valStr = String(v);
        if (predefinedValues.has(valStr)) {
          return valStr;
        } else if (valStr === otherLabelVal) {
          return otherLabelVal;
        } else {
          customValue = valStr;
          return otherLabelVal;
        }
      });

      const hasOther = cleanedValues.includes(otherLabelVal);
      this._otherSelected.set(hasOther);
      if (hasOther) {
        this._otherValue.set(customValue);
        this._otherFormControl.setValue(customValue, { emitEvent: false });
        this._value.set([...new Set(cleanedValues)]);
      } else {
        this._otherValue.set('');
        this._otherFormControl.setValue('', { emitEvent: false });
        this._value.set(cleanedValues);
      }
    } else if (value !== null && value !== undefined && value !== '') {
      const valStr = String(value);
      const predefinedValues = new Set(
        this.chipsData()
          .map((c) => this._getChipValue(c))
          .filter((v) => v !== otherLabelVal)
      );

      if (predefinedValues.has(valStr)) {
        this._otherSelected.set(false);
        this._otherValue.set('');
        this._otherFormControl.setValue('', { emitEvent: false });
        this._value.set(valStr);
      } else {
        this._otherSelected.set(true);
        const customVal = valStr === otherLabelVal ? '' : valStr;
        this._otherValue.set(customVal);
        this._otherFormControl.setValue(customVal, { emitEvent: false });
        this._value.set(otherLabelVal);
      }
    } else {
      this._otherSelected.set(false);
      this._otherValue.set('');
      this._otherFormControl.setValue('', { emitEvent: false });
      this._value.set(null);
    }
  }
  override registerOnChange(fn: any) {
    this._onChange = fn;
  }
  override registerOnTouched(fn: any) {
    this._onTouched = fn;
  }
}
